import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
} from "react-router";

import {
  MdClose,
} from "react-icons/md";

import {
  DgaButton,
  DgaTextarea,
} from "platformscode-new-react/dist/components/stencil-generated/components";

import {
  showDgaSuccess,
  showDgaWarning,
} from "../DgaNotification/DgaNotification";

import "./PageFeedback.css";

const FEEDBACK_STORAGE_KEY =
  "ets-page-feedback";

const POSITIVE_REASONS = [
  {
    value: "helpful",
    label: "كانت مفيدة",
  },
  {
    value: "relevant",
    label:
      "الإجابات كانت مرتبطة",
  },
  {
    value: "clear-content",
    label:
      "صياغة المحتوى متقن",
  },
  {
    value: "well-written",
    label:
      "مكتوبة بشكل جيد",
  },
  {
    value: "other",
    label: "سبب آخر",
  },
];

const NEGATIVE_REASONS = [
  {
    value:
      "technical-problem",
    label:
      "توجد مشكلة تقنية",
  },
  {
    value: "poorly-written",
    label:
      "مكتوبة بشكل سيء",
  },
  {
    value:
      "difficult-design",
    label:
      "التصميم جعله من الصعب قراءته",
  },
  {
    value: "other",
    label: "سبب آخر",
  },
];

const PageFeedback = ({
  onSubmitFeedback,
}) => {
  const location =
    useLocation();

  const closeTimerRef =
    useRef(null);

  const textareaRef =
    useRef(null);

  const [
    answer,
    setAnswer,
  ] = useState(null);

  const [
    expanded,
    setExpanded,
  ] = useState(false);

  const [
    reasons,
    setReasons,
  ] = useState([]);

  const [
    reasonError,
    setReasonError,
  ] = useState("");

  const [
    comment,
    setComment,
  ] = useState("");

  const [
    gender,
    setGender,
  ] = useState("");

  const [
    commentError,
    setCommentError,
  ] = useState("");

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const handleCommentInput =
    useCallback((event) => {
      setComment(
        event?.target?.value ??
          ""
      );

      setCommentError("");
    }, []);

  const reasonOptions =
    answer === "yes"
      ? POSITIVE_REASONS
      : NEGATIVE_REASONS;

  useEffect(() => {
    setExpanded(false);
    setAnswer(null);
    setReasons([]);
    setReasonError("");
    setComment("");
    setGender("");
    setCommentError("");
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (
        closeTimerRef.current
      ) {
        window.clearTimeout(
          closeTimerRef.current
        );
      }
    };
  }, []);

  /*
   * Configure the official DGA textarea
   * without changing its validation state.
   */
  useEffect(() => {
    let active = true;

    const configureTextarea =
      async () => {
        const host =
          textareaRef.current;

        if (!host) {
          return;
        }

        if (
          typeof host.componentOnReady ===
          "function"
        ) {
          await host.componentOnReady();
        }

        if (!active) {
          return;
        }

        host.onInput =
          handleCommentInput;

        const shadowRoot =
          host.shadowRoot;

        const textarea =
          shadowRoot?.querySelector(
            "textarea"
          );

        if (!textarea) {
          return;
        }

        textarea.id =
          "page-feedback-comment";

        textarea.setAttribute(
          "aria-invalid",
          commentError
            ? "true"
            : "false"
        );

        if (commentError) {
          textarea.setAttribute(
            "aria-describedby",
            "page-feedback-comment-error"
          );
        } else {
          textarea.removeAttribute(
            "aria-describedby"
          );
        }

        /*
         * Keep the official DGA error border,
         * but remove the oversized feedback icon
         * to match the approved screenshot.
         */
        if (
          shadowRoot &&
          !shadowRoot.querySelector(
            "#page-feedback-textarea-fix"
          )
        ) {
          const style =
            document.createElement(
              "style"
            );

          style.id =
            "page-feedback-textarea-fix";

          style.textContent = `
            .dga-textarea__feedback-icon--error {
              display: none !important;
            }

            .dga-textarea__field {
              min-height: 110px !important;
            }
          `;

          shadowRoot.appendChild(
            style
          );
        }
      };

    configureTextarea();

    return () => {
      active = false;
    };
  }, [
    answer,
    commentError,
    handleCommentInput,
  ]);

  const handleAnswer = (
    selectedAnswer
  ) => {
    if (
      closeTimerRef.current
    ) {
      window.clearTimeout(
        closeTimerRef.current
      );
    }

    if (
      selectedAnswer !==
      answer
    ) {
      setReasons([]);
      setReasonError("");
      setCommentError("");
    }

    setAnswer(
      selectedAnswer
    );

    setExpanded(true);
  };

  const handleClose = () => {
    setExpanded(false);

    closeTimerRef.current =
      window.setTimeout(
        () => {
          setAnswer(null);
          setReasons([]);
          setReasonError("");
          setComment("");
          setGender("");
          setCommentError("");
        },
        320
      );
  };

  const handleReasonChange = (
    reasonValue
  ) => {
    setReasons(
      (currentReasons) =>
        currentReasons.includes(
          reasonValue
        )
          ? currentReasons.filter(
              (item) =>
                item !==
                reasonValue
            )
          : [
              ...currentReasons,
              reasonValue,
            ]
    );

    setReasonError("");
  };

  const handleSubmit =
    async () => {
      const trimmedComment =
        comment.trim();

      let hasError = false;

      if (
        reasons.length === 0
      ) {
        setReasonError(
          "يرجى تحديد الاختيار"
        );

        hasError = true;
      }

      if (!trimmedComment) {
        setCommentError(
          "الحقل مطلوب"
        );

        hasError = true;
      }

      if (hasError) {
        return;
      }

      const feedbackPayload = {
        isHelpful:
          answer === "yes",

        reasons,

        comment:
          trimmedComment,

        gender:
          gender || null,

        pagePath:
          location.pathname,

        pageTitle:
          document.title,

        submittedAt:
          new Date().toISOString(),
      };

      setSubmitting(true);

      try {
        if (
          typeof onSubmitFeedback ===
          "function"
        ) {
          await onSubmitFeedback(
            feedbackPayload
          );
        } else {
          saveFeedbackLocally(
            feedbackPayload
          );
        }

        showDgaSuccess(
          "شكراً لك، تم إرسال تقييمك بنجاح."
        );

        handleClose();
      } catch (error) {
        console.error(
          "Unable to submit page feedback:",
          error
        );

        showDgaWarning(
          "تعذر إرسال تقييمك. يرجى المحاولة مرة أخرى."
        );
      } finally {
        setSubmitting(false);
      }
    };

  return (
    <section
      className="page-feedback"
      aria-label="تقييم الصفحة"
    >
      <div className="page-feedback__question-bar">
        <div className="page-feedback__question">
          <span>
            هل كانت هذه الصفحة
            مفيدة؟
          </span>

          <div className="page-feedback__choices">
            <DgaButton
              type="button"
              size="md"
              variant="primary-brand"
              label="نعم"
              aria-pressed={
                answer === "yes"
              }
              aria-expanded={
                expanded
              }
              aria-controls="page-feedback-form"
              onOnClick={() => {
                handleAnswer(
                  "yes"
                );
              }}
            />

            <DgaButton
              type="button"
              size="md"
              variant="primary-brand"
              label="لا"
              aria-pressed={
                answer === "no"
              }
              aria-expanded={
                expanded
              }
              aria-controls="page-feedback-form"
              onOnClick={() => {
                handleAnswer(
                  "no"
                );
              }}
            />
          </div>
        </div>

        {expanded ? (
          <button
            type="button"
            className="page-feedback__close"
            onClick={handleClose}
            aria-label="إغلاق نموذج التقييم"
          >
            <span>إغلاق</span>

            <MdClose aria-hidden="true" />
          </button>
        ) : (
          <p className="page-feedback__intro">
            36.8% من المستخدمين
            قالوا نعم من 1,829
            تعليقًا
          </p>
        )}
      </div>

      <div
        id="page-feedback-form"
        className={`page-feedback__drawer ${
          expanded
            ? "page-feedback__drawer--open"
            : ""
        }`}
        aria-hidden={
          !expanded
        }
      >
        <div className="page-feedback__drawer-overflow">
          <div className="page-feedback__drawer-content">
            {answer && (
              <form
                className="page-feedback__form"
                onSubmit={(
                  event
                ) => {
                  event.preventDefault();

                  handleSubmit();
                }}
                noValidate
              >
                <div className="page-feedback__reasons">
                  <h2>
                    من فضلك أخبرنا
                    بالسبب

                    <span>
                      (يمكنك تحديد
                      خيارات متعددة)
                    </span>
                  </h2>

                  <div className="page-feedback__reason-list">
                    {reasonOptions.map((reason) => (
                      <label
                        key={reason.value}
                        className="page-feedback__check-option"
                      >
                        <input
                          type="checkbox"
                          className="page-feedback__native-check"
                          value={reason.value}
                          checked={reasons.includes(
                            reason.value
                          )}
                          onChange={() =>
                            handleReasonChange(
                              reason.value
                            )
                          }
                        />

                        <span
                          className="page-feedback__checkbox-control"
                          aria-hidden="true"
                        />

                        <span className="page-feedback__option-text">
                          {reason.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  {reasonError && (
                    <span
                      className="page-feedback__reason-error"
                      role="alert"
                    >
                      {reasonError}
                    </span>
                  )}

                <div
                  className="page-feedback__gender"
                  role="radiogroup"
                  aria-label="الجنس"
                >
                  <span className="page-feedback__gender-title">
                    أنا
                  </span>

                  <label className="page-feedback__radio-option">
                    <input
                      type="radio"
                      className="page-feedback__native-radio"
                      name="feedbackGender"
                      value="male"
                      checked={gender === "male"}
                      onChange={(event) =>
                        setGender(event.target.value)
                      }
                    />

                    <span
                      className="page-feedback__radio-control"
                      aria-hidden="true"
                    />

                    <span className="page-feedback__option-text">
                      ذكر
                    </span>
                  </label>

                  <label className="page-feedback__radio-option">
                    <input
                      type="radio"
                      className="page-feedback__native-radio"
                      name="feedbackGender"
                      value="female"
                      checked={gender === "female"}
                      onChange={(event) =>
                        setGender(event.target.value)
                      }
                    />

                    <span
                      className="page-feedback__radio-control"
                      aria-hidden="true"
                    />

                    <span className="page-feedback__option-text">
                      أنثى
                    </span>
                  </label>
                  </div> 

                  <p className="page-feedback__privacy">
                    لمزيد من المعلومات
                    يمكنك مراجعة

                    <span>
                      <a
                        href="https://my.gov.sa/ar/content/e-participation#section-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        بيان المشاركة الإلكترونية
                      </a>

                      {" و "}

                      <a
                        href="https://my.gov.sa/ar/content/subscribe#section-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        قواعد الاشتراك
                      </a>

                      {"."}
                    </span>
                  </p>
                </div>

                <div className="page-feedback__experience">
                  <label
                    htmlFor="page-feedback-comment"
                  >
                    أخبرنا عن تجربتك في
                    هذه الخدمة

                    <span
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </label>

                  <DgaTextarea
                    ref={
                      textareaRef
                    }
                    name="feedbackComment"
                    rows={4}
                    value={comment}
                    variant="default"
                    fullwidth
                    resize
                    scrollbar
                    error={Boolean(
                      commentError
                    )}
                    onInput={
                      handleCommentInput
                    }
                  />

                  {commentError && (
                    <span
                      id="page-feedback-comment-error"
                      className="page-feedback__error"
                      role="alert"
                    >
                      {commentError}
                    </span>
                  )}
                </div>

                <div className="page-feedback__actions">
                  <DgaButton
                    type="button"
                    size="md"
                    variant="primary-brand"
                    label={
                      submitting
                        ? "جاري الإرسال..."
                        : "إرسال"
                    }
                    disabled={
                      submitting
                    }
                    onOnClick={
                      handleSubmit
                    }
                  />
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const getSavedPageFeedback =
  () => {
    try {
      const storedFeedback =
        JSON.parse(
          localStorage.getItem(
            FEEDBACK_STORAGE_KEY
          ) || "[]"
        );

      return Array.isArray(
        storedFeedback
      )
        ? storedFeedback
        : [];
    } catch {
      return [];
    }
  };

const saveFeedbackLocally = (
  feedbackPayload
) => {
  const savedFeedback =
    getSavedPageFeedback();

  localStorage.setItem(
    FEEDBACK_STORAGE_KEY,
    JSON.stringify(
      [
        feedbackPayload,
        ...savedFeedback,
      ].slice(0, 100)
    )
  );
};

export default PageFeedback;